import type { JSX, ReactNode } from 'react';
import { Alert, CircularProgress, Stack, Typography } from '@mui/material';
import { Outlet } from 'react-router-dom';

import { useAuth } from './AuthProvider';
import { canAccessWebModule, type ModuleKey } from './moduleAccessPolicy';

export interface RequireModuleAccessProps {
  module: ModuleKey;
  moduleName: string;
  children?: ReactNode;
}

export function RequireModuleAccess({
  module,
  moduleName,
  children,
}: RequireModuleAccessProps): JSX.Element {
  const { currentUser, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ minHeight: '60vh' }}>
        <CircularProgress />
        <Typography color="text.secondary">Loading your session...</Typography>
      </Stack>
    );
  }

  if (!currentUser) {
    return (
      <Stack spacing={2.5}>
        <Typography variant="h4">{moduleName}</Typography>
        <Alert severity="error">
          We could not load your user context. Please sign in again to continue.
        </Alert>
      </Stack>
    );
  }

  if (!canAccessWebModule(currentUser, module)) {
    return (
      <Stack spacing={2.5}>
        <Typography variant="h4">Not authorized</Typography>
        <Alert severity="warning">
          You do not have access to {moduleName} for the selected company.
        </Alert>
      </Stack>
    );
  }

  if (children !== undefined) {
    return <>{children}</>;
  }

  return <Outlet />;
}
